import { motion } from 'framer-motion'
import { useState, useEffect, useRef } from 'react'
import { Calculator, Timer, RotateCcw, Award, Star } from 'lucide-react'

const CalculMental = () => {
  const [gameState, setGameState] = useState('idle') // idle, playing, finished
  const [score, setScore] = useState(0)
  const [timeLeft, setTimeLeft] = useState(60)
  const [currentProblem, setCurrentProblem] = useState(null)
  const [userAnswer, setUserAnswer] = useState('')
  const [message, setMessage] = useState('')
  const [streak, setStreak] = useState(0)
  const [problemsSolved, setProblemsSolved] = useState(0)
  const timerRef = useRef(null)
  const inputRef = useRef(null)

  // Générer un problème de calcul aléatoire
  const generateProblem = () => {
    const operations = ['+', '-', '×']
    const operation = operations[Math.floor(Math.random() * operations.length)]
    
    let num1, num2, answer
    
    switch(operation) {
      case '+':
        num1 = Math.floor(Math.random() * 50) + 1
        num2 = Math.floor(Math.random() * 50) + 1
        answer = num1 + num2
        break
      case '-':
        num1 = Math.floor(Math.random() * 100) + 1
        num2 = Math.floor(Math.random() * num1) + 1
        answer = num1 - num2
        break
      case '×':
        num1 = Math.floor(Math.random() * 12) + 1
        num2 = Math.floor(Math.random() * 12) + 1
        answer = num1 * num2
        break
      default:
        num1 = 1
        num2 = 1
        answer = 2
    }
    
    return {
      num1,
      num2,
      operation,
      answer,
      text: `${num1} ${operation} ${num2} = ?`
    }
  }

  // Démarrer le jeu
  const startGame = () => {
    setGameState('playing')
    setScore(0)
    setTimeLeft(60)
    setStreak(0)
    setProblemsSolved(0)
    setMessage('Résolvez le plus de calculs possible !')
    setCurrentProblem(generateProblem())
    setUserAnswer('')
    
    // Focus sur l'input
    setTimeout(() => {
      inputRef.current?.focus()
    }, 100)

    // Démarrer le timer
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          endGame()
          return 0
        }
        return prev - 1
      })
    }, 1000)
  }

  // Vérifier la réponse
  const checkAnswer = () => {
    if (!userAnswer.trim() || gameState !== 'playing') return

    const userAnswerNum = parseInt(userAnswer)
    
    if (userAnswerNum === currentProblem.answer) {
      // Bonne réponse
      const points = 10 + (streak * 2)
      const newScore = score + points
      const newStreak = streak + 1
      const newProblemsSolved = problemsSolved + 1
      
      setScore(newScore)
      setStreak(newStreak)
      setProblemsSolved(newProblemsSolved)
      setMessage(`Bravo ! +${points} points (Série: ${newStreak})`)
      
      setTimeout(() => {
        setCurrentProblem(generateProblem())
        setUserAnswer('')
        setMessage('')
        inputRef.current?.focus()
      }, 1000)
    } else {
      // Mauvaise réponse
      setStreak(0)
      setMessage(`Incorrect ! La réponse était ${currentProblem.answer}`)
      
      setTimeout(() => {
        setCurrentProblem(generateProblem())
        setUserAnswer('')
        setMessage('')
        inputRef.current?.focus()
      }, 1500)
    }
  }

  // Gérer la touche Entrée
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      checkAnswer()
    }
  }

  // Terminer le jeu
  const endGame = () => {
    clearInterval(timerRef.current)
    setGameState('finished')
    setMessage(`Jeu terminé ! ${problemsSolved} problèmes résolus`)
  }

  // Nettoyer l'intervalle
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
      }
    }
  }, [])

  return (
    <section id="calcul-mental" className="py-20 bg-gradient-to-br from-green-50 to-blue-50">
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <div className="flex justify-center items-center gap-3 mb-4">
            <Calculator className="w-8 h-8 text-primary" />
            <h2 className="section-title">Défi Calcul Mental</h2>
          </div>
          <p className="section-subtitle max-w-2xl mx-auto">
            Testez votre rapidité et votre précision en calcul mental
          </p>
        </motion.div>

        <div className="max-w-4xl mx-auto">
          {/* Carte du jeu */}
          <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8">
            {/* En-tête du jeu */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="bg-blue-50 rounded-lg p-4 text-center">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <Timer className="w-4 h-4 text-blue-600" />
                  <span className="text-sm font-medium text-blue-600">Temps</span>
                </div>
                <div className="text-2xl font-bold text-blue-700">{timeLeft}s</div>
              </div>

              <div className="bg-green-50 rounded-lg p-4 text-center">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <Award className="w-4 h-4 text-green-600" />
                  <span className="text-sm font-medium text-green-600">Score</span>
                </div>
                <div className="text-2xl font-bold text-green-700">{score}</div>
              </div>

              <div className="bg-purple-50 rounded-lg p-4 text-center">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <Star className="w-4 h-4 text-purple-600" />
                  <span className="text-sm font-medium text-purple-600">Série</span>
                </div>
                <div className="text-2xl font-bold text-purple-700">{streak}</div>
              </div>

              <div className="bg-orange-50 rounded-lg p-4 text-center">
                <div className="text-sm font-medium text-orange-600 mb-1">Résolus</div>
                <div className="text-2xl font-bold text-orange-700">{problemsSolved}</div>
              </div>
            </div>

            {/* Zone de jeu */}
            <div className="text-center mb-8">
              <div className={`text-lg font-semibold mb-6 min-h-[24px] ${
                message.includes('Bravo') ? 'text-green-600' : 
                message.includes('Incorrect') ? 'text-red-600' : 'text-gray-700'
              }`}>
                {message || ' '}
              </div>

              {/* Problème actuel */}
              {gameState === 'playing' && currentProblem && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="mb-8"
                >
                  <div className="text-4xl md:text-5xl font-bold text-gray-800 mb-8 p-6 bg-gray-50 rounded-2xl">
                    {currentProblem.text}
                  </div>
                  
                  <div className="max-w-xs mx-auto">
                    <input
                      ref={inputRef}
                      type="number"
                      value={userAnswer}
                      onChange={(e) => setUserAnswer(e.target.value)}
                      onKeyPress={handleKeyPress}
                      disabled={gameState !== 'playing'}
                      className="w-full px-4 py-3 text-2xl text-center border-2 border-gray-300 rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
                      placeholder="Votre réponse"
                      autoFocus
                    />
                  </div>
                </motion.div>
              )}

              {/* Résultat final */}
              {gameState === 'finished' && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-8 p-6 bg-gradient-to-r from-primary to-purple-600 rounded-2xl text-white"
                >
                  <h3 className="text-2xl font-bold mb-2">Résultats Finaux</h3>
                  <div className="grid grid-cols-2 gap-4 text-lg">
                    <div>Score total:</div>
                    <div className="font-bold">{score} points</div>
                    <div>Problèmes résolus:</div>
                    <div className="font-bold">{problemsSolved}</div>
                    <div>Meilleure série:</div>
                    <div className="font-bold">{streak}</div>
                    <div>Points par problème:</div>
                    <div className="font-bold">{problemsSolved > 0 ? Math.round(score / problemsSolved) : 0}</div>
                  </div>
                </motion.div>
              )}
            </div>

            {/* Boutons de contrôle */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {gameState === 'idle' || gameState === 'finished' ? (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={startGame}
                  className="btn-primary flex items-center justify-center gap-2 py-3 px-8"
                >
                  <Calculator className="w-5 h-5" />
                  {gameState === 'finished' ? 'Rejouer' : 'Commencer le défi'}
                </motion.button>
              ) : (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={checkAnswer}
                  disabled={!userAnswer.trim()}
                  className={`flex items-center justify-center gap-2 py-3 px-8 rounded-lg font-semibold transition-all ${
                    userAnswer.trim() 
                      ? 'bg-green-500 text-white hover:bg-green-600 cursor-pointer' 
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  Valider (Entrée)
                </motion.button>
              )}
              
              {gameState === 'playing' && (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={startGame}
                  className="btn-secondary flex items-center justify-center gap-2 py-3 px-8"
                >
                  <RotateCcw className="w-5 h-5" />
                  Redémarrer
                </motion.button>
              )}
            </div>

            {/* Instructions */}
            <div className="mt-8 p-4 bg-gray-50 rounded-lg">
              <h4 className="font-semibold text-gray-800 mb-2">Comment jouer :</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Résolvez le plus de calculs possible en 60 secondes</li>
                <li>• Utilisez le clavier ou les boutons pour répondre</li>
                <li>• Appuyez sur "Entrée" ou cliquez sur "Valider" pour soumettre</li>
                <li>• Chaque bonne réponse rapporte 10 points + bonus de série</li>
                <li>• Les séries continues augmentent vos points !</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default CalculMental